import { resolveDisplayName } from "@/features/data-hub/model/semanticDisplayNames";

export type SemanticOntologyNode = {
  key?: string;
  name?: string;
  description?: string;
  parent_key?: string | null;
  order?: number | null;
  /** Index of the level this value is at, in a multi-column ontology. */
  level?: number | null;
  source_ref?: Record<string, unknown>;
};

/** One column of a multi-column ontology, broadest first (category > sub category > ...). */
export type SemanticOntologyLevel = {
  key?: string;
  name?: string;
  source_field?: string;
};

export const ONTOLOGY_VALUES_SOURCE = {
  MANUAL: "manual",
  SQL: "sql",
} as const;

export type OntologyValuesSource =
  (typeof ONTOLOGY_VALUES_SOURCE)[keyof typeof ONTOLOGY_VALUES_SOURCE];

export const ONTOLOGY_REFRESH_STATUS = {
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

/** The query a SQL-sourced ontology takes its values from, and how its last refresh went. */
export type SemanticOntologyValuesQuery = {
  sql?: string;
  /** Five-field UTC cron; `null` refreshes only on demand. */
  schedule?: string | null;
  last_refreshed_at?: string | null;
  last_status?: (typeof ONTOLOGY_REFRESH_STATUS)[keyof typeof ONTOLOGY_REFRESH_STATUS] | null;
  last_error?: string | null;
  last_row_count?: number | null;
};

export type SemanticOntology = {
  key?: string;
  name?: string;
  description?: string;
  category?: string;
  is_ordered?: boolean;
  source_field?: string | null;
  entity?: string;
  levels?: SemanticOntologyLevel[];
  values_source?: OntologyValuesSource;
  values_query?: SemanticOntologyValuesQuery | null;
  nodes?: SemanticOntologyNode[];
  confidence?: number;
  provenance?: string;
};

export type OntologyLevelFormValue = {
  name: string;
  sourceField: string;
};

export type SemanticOntologyFormValues = {
  name: string;
  description: string;
  category: string;
  isOrdered: boolean;
  sourceField: string;
  entity: string;
  /** Typed values: one per line, or one `A > B > C` path per line for a multi-column ontology. */
  nodes: string;
  isHierarchical: boolean;
  levels: OntologyLevelFormValue[];
  valuesSource: OntologyValuesSource;
  sql: string;
  scheduleEnabled: boolean;
  schedule: string;
};

/** Separates the levels of one value path when a multi-column ontology's values are typed. */
export const ONTOLOGY_PATH_SEPARATOR = ">";
/** A new multi-column ontology starts with this many levels; more are added with "+". */
const MIN_HIERARCHY_LEVELS = 2;
/** Refresh schedule a SQL-sourced ontology starts with when scheduling is switched on. */
export const DEFAULT_ONTOLOGY_REFRESH_SCHEDULE = "0 2 * * *";
// Node-key path joiner and blank-step segment. Must match the backend's `ontology_tree`, so a
// typed tree and a refreshed one key the same value the same way.
const NODE_PATH_SEPARATOR = "__";
const BLANK_SEGMENT = "blank";

export const ONTOLOGY_CATEGORY_OPTIONS = [
  "Product",
  "Marketing",
  "Finance",
  "Operations",
] as const;

export const ONTOLOGY_ACCENT_COLORS = [
  "#3B82F6",
  "#22C55E",
  "#EAB308",
  "#A855F7",
  "#06B6D4",
  "#F97316",
] as const;

const CATEGORY_ACCENT: Record<string, string> = {
  Product: "#3B82F6",
  Marketing: "#22C55E",
  Finance: "#F97316",
  Operations: "#94A3B8",
};

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

export function slugifyOntologyKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function resolveOntologyAccentColor(
  ontology: SemanticOntology,
  index = 0,
): string {
  const category = (ontology.category ?? "").trim();
  if (category && CATEGORY_ACCENT[category]) {
    return CATEGORY_ACCENT[category];
  }
  return ONTOLOGY_ACCENT_COLORS[index % ONTOLOGY_ACCENT_COLORS.length];
}

export function isHierarchicalOntology(ontology: SemanticOntology): boolean {
  return (ontology.levels?.length ?? 0) >= MIN_HIERARCHY_LEVELS;
}

export function isSqlSourcedOntology(ontology: SemanticOntology): boolean {
  return ontology.values_source === ONTOLOGY_VALUES_SOURCE.SQL;
}

function emptyLevel(): OntologyLevelFormValue {
  return { name: "", sourceField: "" };
}

export function createEmptyOntologyLevels(): OntologyLevelFormValue[] {
  return Array.from({ length: MIN_HIERARCHY_LEVELS }, emptyLevel);
}

/** The column part of `DB.SCHEMA.TABLE.COLUMN`. */
function columnOf(sourceField: string): string {
  const parts = sourceField.split(".");
  return parts[parts.length - 1] ?? sourceField;
}

/** Every root-to-end path of a multi-column ontology's tree, one name per level ("" for none). */
export function ontologyValuePaths(ontology: SemanticOntology): string[][] {
  const depth = ontology.levels?.length ?? 0;
  const nodes = (ontology.nodes ?? []).filter(
    (node) => typeof node.key === "string" && typeof node.level === "number",
  );
  const byKey = new Map(nodes.map((node) => [node.key as string, node]));
  const parents = new Set(nodes.map((node) => node.parent_key).filter(Boolean));
  const paths: string[][] = [];
  for (const node of nodes) {
    if (parents.has(node.key)) {
      continue;
    }
    const names = Array.from({ length: depth }, () => "");
    const seen = new Set<string>();
    let current: SemanticOntologyNode | undefined = node;
    while (current && !seen.has(current.key as string)) {
      seen.add(current.key as string);
      const level = current.level as number;
      if (level >= 0 && level < depth) {
        names[level] = current.name ?? "";
      }
      current = current.parent_key ? byKey.get(current.parent_key) : undefined;
    }
    paths.push(names);
  }
  return paths;
}

function comparePaths(left: string[], right: string[]): number {
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    if (left[index] !== right[index]) {
      return left[index] < right[index] ? -1 : 1;
    }
  }
  return left.length - right.length;
}

/**
 * The tree of distinct path prefixes over `rows`, as the backend's `build_hierarchy_nodes` builds
 * it: one node per prefix keyed by its path, a blank step where a middle value is missing, and
 * trailing empty values dropped.
 */
export function buildHierarchyNodes(
  rows: string[][],
  depth: number,
): SemanticOntologyNode[] {
  const unique = new Map<string, string[]>();
  for (const row of rows) {
    const values = Array.from({ length: depth }, (_, index) => (row[index] ?? "").trim());
    while (values.length > 0 && !values[values.length - 1]) {
      values.pop();
    }
    if (values.length > 0) {
      unique.set(JSON.stringify(values), values);
    }
  }
  const paths = [...unique.values()].sort(comparePaths);
  const nodes: SemanticOntologyNode[] = [];
  const keyByPrefix = new Map<string, string>();
  const usedKeys = new Set<string>();
  for (const path of paths) {
    let parentKey: string | null = null;
    path.forEach((value, level) => {
      const prefix = JSON.stringify(path.slice(0, level + 1));
      const existing = keyByPrefix.get(prefix);
      if (existing !== undefined) {
        parentKey = existing;
        return;
      }
      const segment = slugifyOntologyKey(value) || BLANK_SEGMENT;
      const base = parentKey === null ? segment : `${parentKey}${NODE_PATH_SEPARATOR}${segment}`;
      let key = base;
      let suffix = 2;
      while (usedKeys.has(key)) {
        key = `${base}_${suffix}`;
        suffix += 1;
      }
      usedKeys.add(key);
      keyByPrefix.set(prefix, key);
      nodes.push({ key, name: value, parent_key: parentKey, level });
      parentKey = key;
    });
  }
  return nodes;
}

function parsePathLines(value: string, depth: number): string[][] {
  return value
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) =>
      line
        .split(ONTOLOGY_PATH_SEPARATOR)
        .map((part) => part.trim())
        .slice(0, depth),
    );
}

function formatPathLines(ontology: SemanticOntology): string {
  return ontologyValuePaths(ontology)
    .sort(comparePaths)
    .map((path) => path.join(` ${ONTOLOGY_PATH_SEPARATOR} `).replace(/\s+$/, ""))
    .join("\n");
}

function parseNodeLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatNodeLines(nodes: SemanticOntologyNode[] | undefined): string {
  return (nodes ?? [])
    .map((node) => (node.name ?? node.key ?? "").trim())
    .filter(Boolean)
    .join("\n");
}

export function ontologyToFormValues(
  ontology: SemanticOntology,
): SemanticOntologyFormValues {
  const isHierarchical = isHierarchicalOntology(ontology);
  const schedule = ontology.values_query?.schedule ?? "";
  return {
    name: (ontology.name ?? "").trim(),
    description: (ontology.description ?? "").trim(),
    category: (ontology.category ?? "").trim(),
    isOrdered: ontology.is_ordered === true,
    sourceField: (ontology.source_field ?? "").trim(),
    entity: (ontology.entity ?? "").trim(),
    nodes: isHierarchical ? formatPathLines(ontology) : formatNodeLines(ontology.nodes),
    isHierarchical,
    levels: isHierarchical
      ? (ontology.levels ?? []).map((level) => ({
          name: (level.name ?? "").trim(),
          sourceField: (level.source_field ?? "").trim(),
        }))
      : createEmptyOntologyLevels(),
    valuesSource: isSqlSourcedOntology(ontology)
      ? ONTOLOGY_VALUES_SOURCE.SQL
      : ONTOLOGY_VALUES_SOURCE.MANUAL,
    sql: ontology.values_query?.sql ?? "",
    scheduleEnabled: schedule !== "",
    schedule: schedule || DEFAULT_ONTOLOGY_REFRESH_SCHEDULE,
  };
}

export function createEmptyOntologyFormValues(): SemanticOntologyFormValues {
  return {
    name: "",
    description: "",
    category: "",
    isOrdered: false,
    sourceField: "",
    entity: "",
    nodes: "",
    isHierarchical: false,
    levels: createEmptyOntologyLevels(),
    valuesSource: ONTOLOGY_VALUES_SOURCE.MANUAL,
    sql: "",
    scheduleEnabled: false,
    schedule: DEFAULT_ONTOLOGY_REFRESH_SCHEDULE,
  };
}

/** The columns the values query must return, in order: one per level, or the source column. */
export function ontologyQueryColumns(values: SemanticOntologyFormValues): string[] {
  return values.isHierarchical
    ? values.levels.map((level) => level.sourceField.trim())
    : [values.sourceField.trim()];
}

/**
 * `SELECT DISTINCT <columns> FROM <table>` when every column is on one table -- the starting point
 * for a values query, which the author is free to edit (a `WHERE`, an `ORDER BY`). `null` when the
 * columns are not all picked or span several tables.
 */
export function suggestOntologyValuesQuery(values: SemanticOntologyFormValues): string | null {
  const columns = ontologyQueryColumns(values);
  if (columns.length === 0 || columns.some((column) => !column.includes("."))) {
    return null;
  }
  const tables = new Set(
    columns.map((column) => column.slice(0, column.lastIndexOf("."))),
  );
  if (tables.size !== 1) {
    return null;
  }
  const [table] = [...tables];
  return `SELECT DISTINCT ${columns.map(columnOf).join(", ")}\nFROM ${table}`;
}

function buildLevels(levels: OntologyLevelFormValue[]): SemanticOntologyLevel[] {
  const usedKeys = new Set<string>();
  return levels.map((level) => {
    const sourceField = level.sourceField.trim();
    const name = level.name.trim() || columnOf(sourceField);
    const base = slugifyOntologyKey(name) || slugifyOntologyKey(columnOf(sourceField)) || "level";
    let key = base;
    let suffix = 2;
    while (usedKeys.has(key)) {
      key = `${base}_${suffix}`;
      suffix += 1;
    }
    usedKeys.add(key);
    return { key, name, source_field: sourceField };
  });
}

function buildHierarchyFromLines(
  values: SemanticOntologyFormValues,
  existing?: SemanticOntology,
): SemanticOntologyNode[] {
  const depth = values.levels.length;
  const descriptions = new Map(
    (existing?.nodes ?? [])
      .filter((node) => node.key && node.description)
      .map((node) => [node.key as string, node.description as string]),
  );
  return buildHierarchyNodes(parsePathLines(values.nodes, depth), depth).map((node) => {
    const description = descriptions.get(node.key as string);
    return description ? { ...node, description } : node;
  });
}

export function buildOntologyFromFormValues(
  values: SemanticOntologyFormValues,
  existing?: SemanticOntology,
): SemanticOntology {
  const trimmedName = values.name.trim();
  const key =
    (existing?.key ?? "").trim() || slugifyOntologyKey(trimmedName);
  const isSql = values.valuesSource === ONTOLOGY_VALUES_SOURCE.SQL;
  const shape = {
    levels: values.isHierarchical ? buildLevels(values.levels) : undefined,
    source_field: values.isHierarchical ? undefined : values.sourceField.trim() || undefined,
    values_source: values.valuesSource,
    // A query's values belong to its refreshes; the server keeps its own copy on save. A new
    // or changed query is run as part of the save, so the editor gets the values back with it.
    values_query: isSql
      ? {
          ...existing?.values_query,
          sql: values.sql.trim(),
          schedule: values.scheduleEnabled ? values.schedule.trim() : null,
        }
      : undefined,
  };
  const base = {
    ...existing,
    key,
    name: trimmedName,
    description: values.description.trim() || undefined,
    category: values.category.trim() || undefined,
    entity: values.entity.trim() || undefined,
    confidence: 1,
    provenance: "interview",
    ...shape,
  };
  if (isSql) {
    return { ...base, is_ordered: values.isOrdered, nodes: existing?.nodes };
  }
  if (values.isHierarchical) {
    const nodes = buildHierarchyFromLines(values, existing);
    return { ...base, is_ordered: false, nodes: nodes.length > 0 ? nodes : undefined };
  }
  const nodeNames = parseNodeLines(values.nodes);
  // Match each line back to the node it came from and carry forward everything this form
  // cannot express — hierarchy (parent_key), per-node descriptions and physical bindings.
  // Rebuilding nodes from names alone silently flattened a generated taxonomy on every save.
  const existingNodesByKey = new Map(
    (existing?.nodes ?? []).map((node) => [
      (node.key ?? slugifyOntologyKey(node.name ?? "")).trim(),
      node,
    ]),
  );
  // One node per key, first line wins. The server's Ontology validator dedupes on save,
  // so a repeated line would otherwise leave the panel showing rows the save had already
  // collapsed — and a line that slugifies to nothing was rejected outright by the API.
  const nodes: SemanticOntologyNode[] = [];
  const seenNodeKeys = new Set<string>();
  for (const name of nodeNames) {
    const nodeKey = slugifyOntologyKey(name);
    if (!nodeKey || seenNodeKeys.has(nodeKey)) {
      continue;
    }
    seenNodeKeys.add(nodeKey);
    const previous = existingNodesByKey.get(nodeKey);
    nodes.push({
      ...previous,
      key: nodeKey,
      name,
      order: values.isOrdered ? nodes.length : previous?.order,
    });
  }

  return {
    ...base,
    is_ordered: values.isOrdered,
    nodes: nodes.length > 0 ? nodes : undefined,
  };
}

export function selectSemanticOntologiesFromGeneratedModel(
  generatedModel: Record<string, unknown>,
): SemanticOntology[] {
  const document = asRecord(generatedModel.document);
  const legacyDraft = asRecord(generatedModel.draft);
  const raw =
    document.ontologies ?? legacyDraft.ontologies;
  if (!Array.isArray(raw)) {
    return [];
  }
  return (raw as SemanticOntology[]).map((ontology) => ({
    ...ontology,
    name: resolveDisplayName(ontology.name, ontology.key) || ontology.name,
  }));
}

export function isSemanticOntologyKeyTaken(
  ontologies: SemanticOntology[],
  key: string,
  excludeIndex: number | null = null,
): boolean {
  const normalized = key.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return ontologies.some((ontology, index) => {
    if (excludeIndex !== null && index === excludeIndex) {
      return false;
    }
    return (ontology.key ?? "").trim().toLowerCase() === normalized;
  });
}

/** How many values an ontology holds: its value combinations, for a multi-column one. */
export function countOntologyNodes(ontology: SemanticOntology): number {
  if (isHierarchicalOntology(ontology)) {
    return ontologyValuePaths(ontology).length;
  }
  return ontology.nodes?.length ?? 0;
}
